import { useCallback, useMemo, useState } from 'react';

/**
 * Multi-select state for a list of items.
 *
 * Selection is tracked by id (string). The hook does not couple to the table;
 * callers render checkboxes and call back into the API as needed.
 *
 * `visibleIds` is the set the header "select all" operates on (i.e. the
 * currently filtered/searched items). Selections of items that are no longer
 * visible are pruned automatically, so a user changing filters doesn't end up
 * with "ghost" selections they can no longer see.
 *
 * @param {Array<Object>} items - the currently visible (filtered + searched) items
 * @param {(item: Object) => string} idAccessor - extracts the id for selection tracking
 */
export default function useBulkSelection(items, idAccessor) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const visibleIds = useMemo(
    () => new Set((items || []).map(idAccessor).filter(Boolean)),
    [items, idAccessor]
  );

  // Prune any selections that have fallen out of the visible set.
  const effectiveSelectedIds = useMemo(() => {
    const next = new Set();
    selectedIds.forEach((id) => {
      if (visibleIds.has(id)) next.add(id);
    });
    return next;
  }, [selectedIds, visibleIds]);

  const isSelected = useCallback(
    (id) => effectiveSelectedIds.has(id),
    [effectiveSelectedIds]
  );

  const toggle = useCallback((id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allCurrentlySelected =
        visibleIds.size > 0 &&
        Array.from(visibleIds).every((id) => prev.has(id));
      const next = new Set(prev);
      if (allCurrentlySelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [visibleIds]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const allSelected = useMemo(
    () =>
      visibleIds.size > 0 &&
      Array.from(visibleIds).every((id) => effectiveSelectedIds.has(id)),
    [visibleIds, effectiveSelectedIds]
  );

  const someSelected = effectiveSelectedIds.size > 0 && !allSelected;

  // What an action on "the list as the user sees it" applies to (e.g. Export to
  // File): the ticked rows, or every visible row when nothing is ticked.
  const selectedOrVisibleIds = Array.from(
    effectiveSelectedIds.size > 0 ? effectiveSelectedIds : visibleIds
  );

  return {
    selectedIds: Array.from(effectiveSelectedIds),
    selectedOrVisibleIds,
    isSelected,
    toggle,
    toggleAll,
    allSelected,
    someSelected,
    count: effectiveSelectedIds.size,
    clear,
  };
}
